/**
 * Hooks behind the session drawer's Diff, Events and message pieces (architecture doc 8.2).
 * The drawer itself (features/sessions/SessionDrawer.tsx) owns the tabs and the `?tab=` param.
 */
import type { Session } from '@orgus/protocol';
import { useState } from 'react';
import type { DiffViewMode } from '@orgus/ui';
import { useMachine } from '../../lib/store.ts';
import { useControlState } from '../control/live.ts';
import { useTeams } from '../teams/model.ts';
import { useClaim, useMe } from './api.ts';
import { sessionControl } from './model.ts';
import type { Control } from './model.ts';

/** What this person may do with this session (origin, claim or owner, role, machine, session control). */
export function useSessionControl(s: Session | undefined): { control: Control | null; me: { id: string; role: string } } {
  const me = useMe();
  const claim = useClaim(s);
  const machine = useMachine(s?.machineId);
  const teams = useTeams().data;
  const controlled = !!useControlState()?.machines[s?.machineId ?? '']?.installed;
  if (!s) return { control: null, me };
  const owner = teams && Object.hasOwn(teams.sessionOwners, s.id) ? teams.sessionOwners[s.id] : null;
  return { control: sessionControl(s, claim, me, machine ? { online: machine.online, name: machine.name } : null, { owner, controlled }), me };
}

let lastDiffMode: DiffViewMode = 'unified';

export { drawerWidthFor as drawerWidth } from './model.ts';

/** Unified or side by side, remembered for the page's life. */
export function useDiffMode(): [DiffViewMode, (m: DiffViewMode) => void] {
  const [m, setM] = useState<DiffViewMode>(lastDiffMode);
  return [
    m,
    (next) => {
      lastDiffMode = next;
      setM(next);
    },
  ];
}
